import { arrayIsEmpty, arrayNotEmpty, isBlank } from 'src/utils/TypeCheckers';

export enum LOG_LEVEL {
  DEBUG,
  INFO,
  WARN,
  ERROR,
}

const FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: '2-digit',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

class LogService {
  logLevel: LOG_LEVEL = LOG_LEVEL.DEBUG;
  instanceId: string = 'LogService-' + new Date().getTime();

  /**
   * ClassName and no message ids then whole close is ignored
   * if has messageId, then only ignores where is match
   * aim: server can send these to an individual players
   */
  ignoreClasses: { [id: string]: string[] } = {
    App: ['MSG010'],
    AppWrapper: [],
    EnvironmentProvider: [],

    SessionProvider: ['MSG00x'],
    SessionStorage: [],
    // EnvironmentProvider: [],
    Login: ['MSG006'],
    MenuService: [],
    // MaintainTableDataService: [],
    // MaintainTableService: [],
    ReportUtils: [],
    NestedTableField: [],
    Table: [],
    // DateField: [],
    TextField: [],
  };

  public setIgnoreClasses(classes: { [id: string]: string[] }) {
    this.ignoreClasses = classes;
  }

  private ignoreMessage(className: string, messageId: string): boolean {
    if (isBlank(className)) {
      return false;
    }
    const messageIds: string[] = this.ignoreClasses[className.split('-')[0]];
    if (!messageIds) {
      return false;
    }
    if (arrayIsEmpty(messageIds)) {
      return true;
    }
    return messageIds.includes(messageId);
  }

  public setLogLevel(level: number) {
    this.logLevel = level;
  }

  private hasConsole(): boolean {
    return console !== undefined;
  }

  public debug(className: string, messageId: string, ...args: unknown[]) {
    if (this.hasConsole() && this.logLevel < 1 && !this.ignoreMessage(className, messageId)) {
      const message = `%c DEBUG[${new Date().toLocaleDateString(
        'en-GB',
        FORMAT_OPTIONS,
      )}] [${className}] [${messageId}] `;

      const array: unknown[] = arrayNotEmpty(args) ? [...args] : [];
      array.unshift('background: #2c9c91; color: #fafafa');
      array.unshift(message);

      console.log(...array);
    }
  }

  public info(className: string, messageId: string, ...args: unknown[]) {
    if (this.hasConsole() && this.logLevel < 2 && !this.ignoreMessage(className, messageId)) {
      const message = `%c INFO[${new Date().toLocaleDateString(
        'en-GB',
        FORMAT_OPTIONS,
      )}] [${className}] [${messageId}] `;

      const array: unknown[] = arrayNotEmpty(args) ? [...args] : [];
      array.unshift('background: #6FA8DC; color: #fafafa');
      array.unshift(message);

      console.log(...array);
    }
  }

  public warn(className: string, messageId: string, ...args: unknown[]) {
    if (this.hasConsole() && this.logLevel < 3 && !this.ignoreMessage(className, messageId)) {
      const message = `%c WARN[${new Date().toLocaleDateString(
        'en-GB',
        FORMAT_OPTIONS,
      )}] [${className}] [${messageId}] `;

      const array: unknown[] = arrayNotEmpty(args) ? [...args] : [];
      array.unshift('background: #cf8232; color: #fafafa');
      array.unshift(message);

      console.error(...array);
    }
  }

  public error(className: string, messageId: string, ...args: unknown[]) {
    if (this.hasConsole() && this.logLevel < 4 && !this.ignoreMessage(className, messageId)) {
      const message = `%c ERROR[${new Date().toLocaleDateString(
        'en-GB',
        FORMAT_OPTIONS,
      )}] [${className}] [${messageId}] `;

      const array: unknown[] = arrayNotEmpty(args) ? [...args] : [];
      array.unshift('background: #F44336; color: #fafafa');
      array.unshift(message);

      console.error(...array);
    }
  }
}

const log = new LogService();

export { log };

export default LogService;
